import React, { createContext, useContext, useMemo, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { t as translate } from '../i18n';

const I18nContext = createContext({ lang: 'no', t: (key, fb) => fb || key, setLang: () => {} });

function getDefaultLang() {
  // 1. Check localStorage override
  const saved = localStorage.getItem('portal_lang');
  if (saved === 'en' || saved === 'no') return saved;
  // 2. Domain-based default
  if (typeof window !== 'undefined' && window.location.hostname === 'portal.ai-reception.io') return 'en';
  return 'no';
}

export function I18nProvider({ children }) {
  const { user } = useAuth();
  const [manualLang, setManualLang] = useState(getDefaultLang);

  // Priority: manual override > company setting > domain default
  const lang = manualLang || user?.company_language || getDefaultLang();

  const setLang = useCallback((newLang) => {
    setManualLang(newLang);
    localStorage.setItem('portal_lang', newLang);
  }, []);

  const value = useMemo(() => ({
    lang,
    t: (key, fallback) => translate(lang, key, fallback),
    isEnglish: lang === 'en',
    isNorwegian: lang === 'no',
    setLang,
  }), [lang, setLang]);

  // Sync <html lang> attribute
  useEffect(() => {
    document.documentElement.lang = lang === 'en' ? 'en' : 'nb';
  }, [lang]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
